"use client"

import { useTranslations } from "next-intl"
import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import { useRouter, usePathname } from "next/navigation"
import { Info, Flame, AlertTriangle } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import { SlideToBid } from "@/components/auction/slide-to-bid"
import { AuctionBidCustomAmountField } from "@/components/auction/auction-bid-custom-amount-field"
import { AuctionLiveChat } from "./auction-live-chat"
import { cn, formatPrice } from "@/lib/utils"
import { getCurrencySymbol, DEFAULT_CURRENCY } from "@/lib/currencies"
import { formatAuctionCountdown } from "@/lib/format-auction-countdown"
import { getAvatarUrl } from "@/lib/getAvatarUrl"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { useAuction, useAuctionActions } from "@/hooks/use-auction"
import { usePaymentDeadline } from "@/hooks/use-payment-deadline"
import { useViewerAuctionState } from "@/hooks/use-viewer-auction-state"
import type { AuctionSlot, BidRow, AuctionChatItem } from "@/lib/auction-types"
import { serverNow } from "@/lib/clock-sync"
import { placeAuctionBid } from "@/lib/auctions/auction-builders"

/**
 * Bid increment suggestions bracketed by the current highest bid.
 * Desktop shows all four tiers; mobile trims to the first three.
 * Pattern follows an eBay-style geometric ladder scaled by magnitude.
 */
function getSuggestedIncrements(current: number): number[] {
  if (current < 5) return [1, 2, 3, 5]
  if (current < 25) return [1, 2, 5, 10]
  if (current < 100) return [2, 5, 10, 25]
  if (current < 250) return [5, 10, 25, 50]
  if (current < 500) return [10, 25, 50, 100]
  if (current < 1000) return [25, 50, 100, 250]
  if (current < 2500) return [50, 100, 250, 500]
  if (current < 5000) return [100, 250, 500, 1000]
  if (current < 10000) return [250, 500, 1000, 2500]
  return [500, 1000, 2500, 5000]
}

export interface AuctionData {
  startPrice: number
  reservePrice: number | null
  endAt: string | null
  currentUserIsBidder?: boolean
  status: string
  endedAt: string | null
  highestBid: number | null
  highestBidderId: string | null
  bidCount: number
  eventVersion: number
  softCloseWindowSeconds: number
  softCloseExtensionSeconds: number
  paymentDeadline: string | null
}

interface AuctionPanelProps {
  listingId: string
  auction: AuctionData
  currency?: string | null
  currentUserId?: string | null
  sellerId: string
  onlyStats?: boolean
  onlyBidBar?: boolean
  onlyChat?: boolean
  showAuctionLiveChat?: boolean
  surfaceVariant?: "card" | "quickSheet"
  omitOuterSurface?: boolean
  chatEmbedded?: boolean
  onPayAsWinner?: () => void
  onRequestClose?: () => void
  quickSheetFooterHost?: HTMLElement | null
  onQuickSheetBidFooterSlotActiveChange?: (active: boolean) => void
}

function nextMinBid(current: number): number {
  if (current >= 1000) return current + Math.max(10, Math.ceil(current * 0.01))
  if (current >= 100) return current + 5
  return current + 1
}

function formatCountdown(endAt: string): { hours: number; minutes: number; seconds: number } | null {
  const end = new Date(endAt).getTime()
  const now = serverNow()
  const diff = Math.max(0, Math.floor((end - now) / 1000))
  if (diff <= 0) return null
  const hours = Math.floor(diff / 3600)
  const minutes = Math.floor((diff % 3600) / 60)
  const seconds = diff % 60
  return { hours, minutes, seconds }
}

export function auctionDataToInitial(auction: AuctionData): Partial<AuctionSlot> {
  return {
    endAt: auction.endAt,
    highestBid: auction.highestBid,
    // FIX-8: seed realtime state with SSR highestBidderId
    highestBidderId: auction.highestBidderId,
    bidCount: auction.bidCount,
    eventVersion: auction.eventVersion,
    auctionStatus: auction.status,
    paymentDeadline: auction.paymentDeadline,
  }
}

function PaymentDeadlineCountdown({ deadline, onExpired }: { deadline: string; onExpired?: () => void }) {
  const t = useTranslations("auction")
  const state = usePaymentDeadline(deadline, onExpired)

  if (!state) return null

  if (state.expired) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-xl bg-red-900/20 border border-red-800/30">
        <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
        <p className="text-sm text-red-200">{t("deadlineExpired")}</p>
      </div>
    )
  }

  const isUrgent = state.hours < 4

  return (
    <div className={`flex items-start gap-2 p-3 rounded-xl ${
      isUrgent ? "bg-amber-900/20 border border-amber-800/30" : "bg-muted/50 border border-border"
    }`}>
      <Info className={`h-4 w-4 mt-0.5 shrink-0 ${isUrgent ? "text-amber-400" : "text-muted-foreground"}`} />
      <p className={`text-sm ${isUrgent ? "text-amber-200" : "text-foreground"}`}>
        {state.expired ? t("deadlineExpired") : state.hours > 0 ? t("timeLeftHours", { hours: state.hours, minutes: state.minutes }) : t("timeLeftMinutes", { minutes: state.minutes })}
      </p>
    </div>
  )
}

interface AuctionPanelInnerProps {
  slot: AuctionSlot
  actions: ReturnType<typeof useAuctionActions>
  listingId: string
  auction: AuctionData
  currency?: string | null
  currentUserId?: string | null
  sellerId: string
  onlyStats?: boolean
  onlyBidBar?: boolean
  onlyChat?: boolean
  showAuctionLiveChat?: boolean
  surfaceVariant?: "card" | "quickSheet"
  omitOuterSurface?: boolean
  chatEmbedded?: boolean
  onPayAsWinner?: () => void
  onRequestClose?: () => void
  quickSheetFooterHost?: HTMLElement | null
  onQuickSheetBidFooterSlotActiveChange?: (active: boolean) => void
}

function AuctionPanelInner({
  slot,
  actions,
  listingId,
  auction,
  currency = "USD",
  currentUserId,
  sellerId,
  onlyStats = false,
  onlyBidBar = false,
  onlyChat = false,
  showAuctionLiveChat = true,
  surfaceVariant = "card",
  omitOuterSurface = false,
  chatEmbedded = false,
  onPayAsWinner,
  onRequestClose,
  quickSheetFooterHost = null,
  onQuickSheetBidFooterSlotActiveChange,
}: AuctionPanelInnerProps) {
  const t = useTranslations("auction")
  const isQuickSheet = surfaceVariant === "quickSheet"
  const quickBidFooterOwnedByParent = Boolean(onQuickSheetBidFooterSlotActiveChange)
  const { highestBid, bidCount, eventVersion, bids, chatItems, auctionStatus: rtStatus, endAt } = slot
  const { patchAuction, refetchBids, refetchChat, refetchSnapshot, sendChatMessage } = actions
  const router = useRouter()
  const pathname = usePathname() ?? "/"
  const safeCallback = ["/login", "/register"].includes(pathname) ? "/" : pathname
  const handleLogin = useCallback(() => {
    onRequestClose?.()
    router.push(`/login?callbackUrl=${encodeURIComponent(safeCallback)}`)
  }, [router, safeCallback, onRequestClose])
  const handleRegister = useCallback(() => {
    onRequestClose?.()
    router.push(`/register?callbackUrl=${encodeURIComponent(safeCallback)}`)
  }, [router, safeCallback, onRequestClose])

  const safeChatItems = chatItems ?? []
  const handleMessageSent = useCallback((payload: { item: AuctionChatItem }) => {
    sendChatMessage(payload.item)
  }, [sendChatMessage])

  const initialCurrent = auction.highestBid ?? auction.startPrice
  const initialIncrements = getSuggestedIncrements(initialCurrent)
  const [selectedIncrement, setSelectedIncrement] = useState(initialIncrements[initialIncrements.length - 1])
  const [customIncrement, setCustomIncrement] = useState("")
  const [customAmountFocused, setCustomAmountFocused] = useState(false)
  const [placing, setPlacing] = useState(false)
  const [flashBid, setFlashBid] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [countryWarning, setCountryWarning] = useState<string | null>(null)
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const triggerFlash = useCallback(() => {
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current)
    setFlashBid(true)
    flashTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) setFlashBid(false)
      flashTimeoutRef.current = null
    }, 500)
  }, [])

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current)
    }
  }, [])

  const current = highestBid ?? auction.startPrice
  const nextMin = useMemo(() => nextMinBid(current), [current])
  const suggestedIncrementsDesktop = useMemo(() => getSuggestedIncrements(current), [current])
  const suggestedIncrementsMobile = useMemo(
    () => suggestedIncrementsDesktop.slice(-3),
    [suggestedIncrementsDesktop],
  )
  const topIncrement = suggestedIncrementsDesktop[suggestedIncrementsDesktop.length - 1]
  useEffect(() => {
    if (customIncrement) return
    setSelectedIncrement(topIncrement)
  }, [topIncrement, customIncrement])
  const increment = customIncrement ? parseInt(customIncrement, 10) : selectedIncrement
  const bidAmount = Number.isFinite(increment) && increment > 0 ? current + increment : nextMin

  const [displayCountdown, setDisplayCountdown] = useState<{ hours: number; minutes: number; seconds: number } | null>(null)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
    if (!endAt) return
    const updateCountdown = () => {
      const c = formatCountdown(endAt)
      setDisplayCountdown(c)
    }
    updateCountdown()
    const t = setInterval(updateCountdown, 1000)
    return () => clearInterval(t)
  }, [endAt])

  // Client-triggered transition: fire when countdown reaches 0
  useEffect(() => {
    if (!isMounted || displayCountdown !== null || !endAt) return
    const effectiveStatusNow = rtStatus ?? auction.status
    if (effectiveStatusNow === "ended" || effectiveStatusNow === "settled") return
    // Skip if endAt is >5 min in the past — cron has already handled it (avoids stale-cache false triggers)
    if (serverNow() - new Date(endAt).getTime() > 5 * 60 * 1000) return
    // Random delay 0-3s to avoid thundering herd when many clients expire simultaneously
    const delay = Math.random() * 3000
    const timer = setTimeout(() => {
      fetch(`/api/auctions/${listingId}/transition`, { method: "POST" }).catch(() => {})
    }, delay)
    return () => clearTimeout(timer)
  }, [isMounted, displayCountdown, endAt, listingId, rtStatus, auction.status])

  // Single source of truth for the viewer projection — see issue #156.
  // All "is ended / can pay / reserve not met / is winner" booleans below are
  // derived from this FSM, not from raw fields, to avoid the drift that caused
  // bug #29 (mobile let the top bidder reach PayPal after reserve-not-met).
  const viewerState = useViewerAuctionState({
    listingId,
    currentUserId: currentUserId ?? null,
    auction,
    sellerId,
  })
  const isEnded = viewerState.kind !== "live" && viewerState.kind !== "scheduled"
  const isSeller = viewerState.kind === "ended_seller" || currentUserId === sellerId
  const liveReserveMet = viewerState.kind === "live" ? viewerState.reserveMet : null
  const reserveNotMetWhenEnded =
    viewerState.kind === "ended_reserve_not_met" ||
    (viewerState.kind === "ended_seller" && viewerState.subKind === "reserve_not_met")
  const isWinnerSettled = viewerState.kind === "ended_won_settled"
  const isWinnerPending = viewerState.kind === "ended_won_pending_payment"
  const winnerDeadline = isWinnerPending ? viewerState.deadline : null

  const isBidder = currentUserId
    ? (bids ?? []).some((b) => b.bidder_id === currentUserId) || auction.currentUserIsBidder === true
    : false
  const canChat = isSeller || isBidder
  const canBid = viewerState.kind === "live" && viewerState.canBid && !placing
  useEffect(() => {
    const cb = onQuickSheetBidFooterSlotActiveChange
    if (!cb) return
    if (!isQuickSheet) {
      cb(false)
      return
    }
    cb(Boolean(canBid))
    return () => {
      cb(false)
    }
  }, [isQuickSheet, canBid, onQuickSheetBidFooterSlotActiveChange])

  const placeBid = useCallback(async () => {
    if (!canBid || bidAmount <= current) return
    setPlacing(true)
    setError(null)
    try {
      // G17 producer: builder lives in `lib/auctions/auction-builders.ts` and
      // runs `PlaceAuctionBidRequestSchema.safeParse` on the wire body before
      // fetch. The `satisfies <ContractType>` pin (Sibling-D D2) moved with
      // the literal — see the builder header for the not-to-be-confused-with
      // server-side `lib/auction-bids.ts` note.
      const result = await placeAuctionBid({
        listingId,
        amount: bidAmount,
        clientBidId: `client-${listingId}-${Date.now()}-${eventVersion}`,
      })
      const data = (result.data ?? {}) as {
        error?: string
        required_min?: unknown
        new_end_at?: unknown
        new_highest_bid?: unknown
        event_version?: unknown
        new_bid_count?: unknown
        countryBanWarning?: unknown
      }
      if (!result.ok) {
        setError(data.error || t("failedBid"))
        const requiredMin = Number(data.required_min)
        if (Number.isFinite(requiredMin)) setSelectedIncrement(Math.ceil(requiredMin - current))
        return
      }
      const patch: Partial<AuctionSlot> = {}
      if (typeof data.new_end_at === "string") patch.endAt = data.new_end_at
      const nextHighestBid = Number(data.new_highest_bid)
      if (Number.isFinite(nextHighestBid)) patch.highestBid = nextHighestBid
      const nextEventVersion = Number(data.event_version)
      if (Number.isFinite(nextEventVersion)) patch.eventVersion = nextEventVersion
      const nextBidCount = Number(data.new_bid_count)
      if (Number.isFinite(nextBidCount)) patch.bidCount = nextBidCount
      if (Object.keys(patch).length > 0) patchAuction(patch)
      if (typeof data.countryBanWarning === "string") {
        setCountryWarning(data.countryBanWarning)
      }
      triggerFlash()
      refetchBids()
      refetchChat()
      if (!Number.isFinite(nextBidCount)) refetchSnapshot()
    } finally {
      setPlacing(false)
    }
  }, [canBid, bidAmount, current, t, listingId, eventVersion, patchAuction, triggerFlash, refetchBids, refetchChat, refetchSnapshot])

  // Lifted from below the `onlyBidBar` early return (was a rules-of-hooks
  // violation: useMemo only ran on the !onlyBidBar branch). Hoisting to the
  // top is safe — leaderboard only depends on `bids`, computed lazily by
  // useMemo, and consumers below still read the same value.
  const leaderboard = useMemo(() => {
    const bidsArr = bids ?? []
    if (bidsArr.length === 0) return []
    const byKey = (b: BidRow) => (b.bidder_id ?? b.bidder_display) || b.id
    const maxByBidder = new Map<string, { display: string; max: number; streak: number; avatar_url?: string | null }>()
    for (const b of bidsArr) {
      const k = byKey(b)
      const cur = maxByBidder.get(k)
      if (!cur) maxByBidder.set(k, { display: b.bidder_display, max: b.amount, streak: 1, avatar_url: b.avatar_url ?? null })
      else {
        cur.display = b.bidder_display
        cur.streak += 1
        if (b.amount > cur.max) cur.max = b.amount
        if (b.avatar_url != null) cur.avatar_url = b.avatar_url
      }
    }
    return Array.from(maxByBidder.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.max - a.max)
      .slice(0, 5)
  }, [bids])

  if (onlyChat) {
    if (!canChat) return null
    return (
      <TooltipProvider>
        <div
          className={
            chatEmbedded
              ? "px-4 pb-4 pt-4 border-t border-border"
              : "rounded-3xl border border-border bg-card p-4"
          }
        >
          <AuctionLiveChat
            listingId={listingId}
            currentUserId={currentUserId}
            currency={currency}
            chatItems={safeChatItems}
            onMessageSent={handleMessageSent}
            canWrite={canChat}
          />
        </div>
      </TooltipProvider>
    )
  }

  if (onlyBidBar) {
    if (!currentUserId && !isEnded) {
      return (
        <div className="flex flex-col gap-2 w-full">
          <Button className="w-full h-12 rounded-full text-base font-semibold" onClick={handleLogin}>
            {t("loginToBid")}
          </Button>
          <Button variant="outline" className="w-full h-12 rounded-full text-base font-semibold border-neutral-600 bg-neutral-800/80 text-white hover:bg-neutral-700 hover:border-neutral-500" onClick={handleRegister}>
            {t("register")}
          </Button>
        </div>
      )
    }
    const sym = getCurrencySymbol(currency ?? DEFAULT_CURRENCY)
    return (
      <div className="space-y-3 w-full">
        <div className="flex flex-wrap items-center gap-2">
          {suggestedIncrementsMobile.map((inc) => (
            <button
              key={inc}
              type="button"
              onClick={() => { setSelectedIncrement(inc); setCustomIncrement("") }}
              className={`rounded-full px-3 py-2 text-sm font-mono transition-colors shrink-0 ${
                selectedIncrement === inc && !customIncrement
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted border border-border text-foreground hover:bg-muted/80"
              }`}
            >
              +{sym}{inc}
            </button>
          ))}
          <AuctionBidCustomAmountField
            currencySym={sym}
            value={customIncrement}
            onChange={(v) => {
              setCustomIncrement(v)
              if (v) setSelectedIncrement(0)
            }}
            focused={customAmountFocused}
            onFocus={() => setCustomAmountFocused(true)}
            onBlur={() => setCustomAmountFocused(false)}
          />
        </div>
        <div className="w-full">
          <SlideToBid bidAmount={bidAmount} currentPrice={current} onConfirm={() => placeBid()} disabled={placing} currency={currency} />
        </div>
        {error && <p className="text-sm text-destructive text-center">{error}</p>}
        {countryWarning && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-900/20 border border-amber-800/30">
            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-200">{countryWarning}</p>
          </div>
        )}
      </div>
    )
  }

  const totalSecondsLeft = displayCountdown
    ? displayCountdown.hours * 3600 + displayCountdown.minutes * 60 + displayCountdown.seconds
    : 0
  const isCloseToEnd = !isEnded && totalSecondsLeft > 0 && totalSecondsLeft <= 60

  const currencySym = getCurrencySymbol(currency ?? DEFAULT_CURRENCY)
  const bidAmountFormatted = formatPrice(String(bidAmount), currency ?? "USD")

  return (
    <TooltipProvider>
      <div
        className={
          isQuickSheet
            ? cn("flex min-h-0 flex-1 flex-col gap-3", isCloseToEnd && !isEnded && "ring-1 ring-red-500/25 rounded-3xl -mx-1 px-1 pt-1 pb-2")
            : omitOuterSurface
              ? "space-y-4 p-4"
              : `space-y-4 rounded-3xl p-4 transition-colors border ${
                  isEnded ? "bg-card border-border" : isCloseToEnd ? "bg-card border-red-500/30" : "bg-card border-border"
                }`
        }
      >
        {!onlyStats && !isEnded && (
          isQuickSheet ? (
            <div className="flex items-start gap-2.5 rounded-2xl bg-neutral-800/90 border border-neutral-700/50 px-3 py-2.5">
              <Info className="h-4 w-4 shrink-0 text-neutral-400 mt-0.5" aria-hidden />
              <p className="text-sm text-neutral-300 leading-snug">
                {t("antiSnipeShort", { seconds: auction.softCloseExtensionSeconds })}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{t("antiSnipeShort", { seconds: auction.softCloseExtensionSeconds })}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="inline-flex text-muted-foreground hover:text-foreground">
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs bg-card border border-border">
                  {t("antiSnipeLong", { window: auction.softCloseWindowSeconds, extend: auction.softCloseExtensionSeconds })}
                </TooltipContent>
              </Tooltip>
            </div>
          )
        )}

        {!isQuickSheet && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{isEnded ? (reserveNotMetWhenEnded ? t("highestBid") : t("winningBid")) : t("currentBid")}</span>
              <div className="flex items-center gap-2">
                {!isEnded && auction.reservePrice != null && (
                  <span className="text-xs text-muted-foreground">
                    {liveReserveMet ? t("reserveMet") : t("reserveNotMet")}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">{t("bids", { count: bidCount })}</span>
              </div>
            </div>
            <div className={`text-4xl font-bold font-mono tabular-nums transition-colors ${flashBid ? "text-green-400" : "text-foreground"}`}>
              {formatPrice(String(highestBid ?? auction.startPrice), currency ?? "USD")}
            </div>
            {highestBid == null && (
              <p className="text-sm text-muted-foreground">
                {t("noBids", { price: formatPrice(String(auction.startPrice), currency ?? "USD") })}
              </p>
            )}

            {!isEnded && isMounted && displayCountdown && (
              <div>
                <span className={`text-xs block mb-1 ${isCloseToEnd ? "text-red-400/70" : "text-muted-foreground"}`}>
                  {t("endsIn")}
                </span>
                <div
                  className={`font-mono text-lg tabular-nums ${isCloseToEnd ? "text-red-400" : "text-foreground"}`}
                  suppressHydrationWarning
                >
                  {formatAuctionCountdown(totalSecondsLeft, {
                    d: t("countdown.d"),
                    h: t("countdown.h"),
                    m: t("countdown.m"),
                    s: t("countdown.s"),
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {isQuickSheet && !onlyStats && !isEnded && (
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-neutral-800/90 border border-neutral-700/50 p-3 text-center min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                {t("quickSheet.statBid")}
              </div>
              <div className={`text-base font-bold font-mono tabular-nums leading-tight ${flashBid ? "text-emerald-400" : "text-white"}`}>
                {formatPrice(String(highestBid ?? auction.startPrice), currency ?? "USD")}
              </div>
              {highestBid == null && (
                <p className="text-[10px] text-neutral-500 mt-1 leading-tight">{t("quickSheet.noBidsYet")}</p>
              )}
            </div>
            <div className="rounded-xl bg-neutral-800/90 border border-neutral-700/50 p-3 text-center min-w-0 flex flex-col justify-center">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                {t("quickSheet.statReserve")}
              </div>
              {auction.reservePrice != null ? (
                <span
                  className={`text-xs font-semibold leading-tight ${
                    liveReserveMet ? "text-emerald-400" : "text-amber-400"
                  }`}
                >
                  {liveReserveMet ? t("reserveMet") : t("reserveNotMet")}
                </span>
              ) : (
                <span className="text-neutral-500 text-sm">—</span>
              )}
            </div>
            <div className="rounded-xl bg-neutral-800/90 border border-neutral-700/50 p-3 text-center min-w-0 flex flex-col justify-center">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">
                {t("quickSheet.statBids")}
              </div>
              <div className="flex items-center justify-center gap-1.5 text-white font-bold font-mono text-lg">
                <Flame className="h-4 w-4 text-orange-400 shrink-0" aria-hidden />
                <span>{bidCount}</span>
              </div>
            </div>
          </div>
        )}

        {reserveNotMetWhenEnded && (
          <div className="p-3 rounded-xl bg-amber-900/20 border border-amber-800/30">
            <p className="text-sm text-amber-200 font-medium">{t("reservePriceNotMet")}</p>
            {viewerState.kind === "ended_seller" && (
              <p className="text-xs text-neutral-400 mt-1">
                {t("reserveNotMetSeller")}
              </p>
            )}
            {viewerState.kind === "ended_reserve_not_met" && viewerState.wasHighestBidder && (
              <p className="text-xs text-neutral-400 mt-1">
                {t("reserveNotMetBidder")}
              </p>
            )}
          </div>
        )}

        {isWinnerSettled && (
          <Button className="w-full rounded-full bg-neutral-800 text-neutral-400 cursor-default" size="lg" disabled>
            {t("paid", { price: formatPrice(String(highestBid ?? auction.startPrice), currency ?? "USD") })}
          </Button>
        )}
        {isWinnerPending && (
          <>
            {winnerDeadline && (
              <PaymentDeadlineCountdown deadline={winnerDeadline} />
            )}
            {onPayAsWinner && (
              <Button className="w-full rounded-full bg-white text-black hover:bg-neutral-200" size="lg" onClick={onPayAsWinner}>
                {t("pay", { price: formatPrice(String(highestBid ?? auction.startPrice), currency ?? "USD") })}
              </Button>
            )}
          </>
        )}
        {viewerState.kind === "ended_payment_expired" && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-900/20 border border-red-800/30">
            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-sm text-red-200">{t("deadlineExpired")}</p>
          </div>
        )}

        {!onlyStats && canBid && (
          isQuickSheet ? (
            <>
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-4 px-4">
                  <div className="space-y-3 pb-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-2">{t("quickSheet.raiseBy")}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {suggestedIncrementsMobile.map((inc) => (
                        <button
                          key={inc}
                          type="button"
                          onClick={() => {
                            setSelectedIncrement(inc)
                            setCustomIncrement("")
                          }}
                          className={`rounded-full px-3 py-2 text-sm font-mono transition-colors shrink-0 ${
                            selectedIncrement === inc && !customIncrement
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted border border-border text-foreground hover:bg-muted/80"
                          }`}
                        >
                          +{currencySym}
                          {inc}
                        </button>
                      ))}
                      <AuctionBidCustomAmountField
                        currencySym={currencySym}
                        value={customIncrement}
                        onChange={(v) => {
                          setCustomIncrement(v)
                          if (v) setSelectedIncrement(0)
                        }}
                        focused={customAmountFocused}
                        onFocus={() => setCustomAmountFocused(true)}
                        onBlur={() => setCustomAmountFocused(false)}
                      />
                    </div>
                    {error && <p className="text-sm text-red-400 text-center">{error}</p>}
                    {countryWarning && (
                      <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-900/25 border border-amber-800/35">
                        <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-sm text-amber-100">{countryWarning}</p>
                      </div>
                    )}
                  </div>
                </div>
                {!quickSheetFooterHost && !quickBidFooterOwnedByParent ? (
                  <div className="shrink-0 bg-neutral-900 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)] -mx-4 px-4">
                    <SlideToBid
                      bidAmount={bidAmount}
                      currentPrice={current}
                      onConfirm={() => placeBid()}
                      disabled={placing}
                      currency={currency}
                      trackLabel={t("slideTrack", { amount: bidAmountFormatted })}
                      confirmingLabel={t("slideConfirming")}
                    />
                  </div>
                ) : null}
              </div>
              {quickSheetFooterHost
                ? createPortal(
                    <SlideToBid
                      bidAmount={bidAmount}
                      currentPrice={current}
                      onConfirm={() => placeBid()}
                      disabled={placing}
                      currency={currency}
                      trackLabel={t("slideTrack", { amount: bidAmountFormatted })}
                      confirmingLabel={t("slideConfirming")}
                    />,
                    quickSheetFooterHost,
                  )
                : null}
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {suggestedIncrementsDesktop.map((inc) => (
                  <button
                    key={inc}
                    type="button"
                    onClick={() => {
                      setSelectedIncrement(inc)
                      setCustomIncrement("")
                    }}
                    className={`rounded-full px-4 py-2 text-sm font-mono transition-colors ${
                      selectedIncrement === inc && !customIncrement
                        ? "bg-foreground text-background"
                        : "bg-muted border border-border hover:border-foreground/40 text-foreground"
                    }`}
                  >
                    +{formatPrice(String(inc), currency ?? "USD")}
                  </button>
                ))}
                <div
                  className={`relative rounded-full border transition-colors ${
                    customAmountFocused || customIncrement ? "border-border ring-1 ring-ring/50" : "border-border"
                  }`}
                >
                  <span
                    className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm transition-colors ${
                      customAmountFocused || customIncrement ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    +{currencySym}
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="..."
                    className="w-24 pl-8 pr-3 py-2 rounded-full bg-muted border-0 text-base md:text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
                    value={customIncrement}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === "" || /^\d+$/.test(v)) setCustomIncrement(v)
                    }}
                    onFocus={() => setCustomAmountFocused(true)}
                    onBlur={() => setCustomAmountFocused(false)}
                  />
                </div>
              </div>
              <SlideToBid
                bidAmount={bidAmount}
                currentPrice={current}
                onConfirm={() => placeBid()}
                disabled={placing}
                currency={currency}
              />
              {error && <p className="text-sm text-red-400">{error}</p>}
              {countryWarning && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-900/20 border border-amber-800/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-200">{countryWarning}</p>
                </div>
              )}
            </>
          )
        )}
        {!onlyStats && !currentUserId && !isEnded && (
          <div className={cn("flex flex-col gap-2", isQuickSheet && "pb-[calc(env(safe-area-inset-bottom)+8px)]")}>
            <Button
              className={cn(
                "w-full h-12 rounded-full text-base font-semibold",
                isQuickSheet && "bg-white text-black hover:bg-white/90",
              )}
              onClick={handleLogin}
            >
              {t("loginToBid")}
            </Button>
            <Button
              variant="outline"
              className={cn(
                "w-full h-12 rounded-full text-base font-semibold",
                isQuickSheet && "border-white/20 bg-neutral-800 text-white hover:bg-neutral-700",
              )}
              onClick={handleRegister}
            >
              {t("register")}
            </Button>
          </div>
        )}
        {!onlyStats && isSeller && !isEnded && (
          <p className={cn("text-sm text-muted-foreground", isQuickSheet && "text-neutral-400")}>{t("sellerCannotBid")}</p>
        )}

        {!isQuickSheet && leaderboard.length > 0 && (
          <div className="space-y-3 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">{t("topBidders")}</span>
              <span className="text-xs text-muted-foreground">{isEnded ? t("final") : t("live")}</span>
            </div>
            <div className="space-y-2">
              {leaderboard.map((entry, i) => {
                const avatarUrl = getAvatarUrl(entry.avatar_url) || undefined
                return (
                <div key={entry.key} className="flex items-center gap-3 text-sm">
                  <span className={`w-5 text-xs font-mono ${i === 0 ? "text-amber-400" : "text-muted-foreground"}`}>#{i + 1}</span>
                  <Avatar className="w-7 h-7 shrink-0 bg-muted text-xs font-bold text-foreground">
                    {avatarUrl && <AvatarImage src={avatarUrl} alt={entry.display || ""} />}
                    <AvatarFallback className="bg-muted text-foreground text-xs font-bold">
                      {(entry.display || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate text-foreground">{entry.display}</span>
                  {entry.streak > 1 && (
                    <span className="flex items-center gap-0.5 text-orange-400 text-xs">
                      <Flame className="w-3 h-3" />
                      <span className="font-mono">{entry.streak}</span>
                    </span>
                  )}
                  <span className="text-sm font-mono text-muted-foreground">{formatPrice(String(entry.max), currency ?? "USD")}</span>
                </div>
              )
              })}
            </div>
          </div>
        )}

        {showAuctionLiveChat && !onlyStats && !onlyBidBar && canChat && (
          <div className="pt-4 border-t border-border">
            <AuctionLiveChat
              listingId={listingId}
              currentUserId={currentUserId}
              currency={currency}
              chatItems={safeChatItems}
              onMessageSent={handleMessageSent}
              canWrite={canChat}
            />
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}

export function AuctionPanel({ listingId, auction, ...props }: AuctionPanelProps) {
  const slot = useAuction(listingId, { initial: auctionDataToInitial(auction), broadcast: true })
  const actions = useAuctionActions(listingId)
  if (!slot) return null
  return <AuctionPanelInner {...props} listingId={listingId} auction={auction} slot={slot} actions={actions} />
}

export function AuctionPanelFromContext(props: AuctionPanelProps) {
  const { listingId, auction } = props
  const slot = useAuction(listingId, { initial: auctionDataToInitial(auction), broadcast: true })
  const actions = useAuctionActions(listingId)
  if (!slot) return null
  return <AuctionPanelInner {...props} slot={slot} actions={actions} />
}
